import React, { createContext, useCallback, useContext, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useParams } from '@reach/router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  GetWurRatingAndScoreQuery,
  ScenarioQuery,
  SubmissionWur2Query,
  SubmissionWur3Query,
  WurDashboardPageQueryQuery,
  WurReporterInterimScenarioQuery,
} from '#types/generated/graphql';
import { Peer } from '#core/PageQuery';
import { useSessionStorage } from '#hooks/useSessionStorage';
import { usePermissions } from '#core/Permissions';
import {
  getSubmissionQueryWur2,
  getSubmissionQueryWur3,
} from '../../createScenario/submissionQuery';
import {
  getScenarioMethodologyYear,
  LevelDefinitionsObjectType,
} from '../../utils';
import {
  getScenarioQuery,
  getWurReporterInterimScenarioQuery,
  scenarioQueryVariables,
  wurQueryVariables,
} from '../scenarioAndWurResultQuery';
import {
  POSSIBLE_METHODOLOGY_YEARS_WUR_EXPLORE,
  POSSIBLE_METHODOLOGY_YEARS_WUR_REPORTER,
  YEAR_OF_START_OF_WUR_3_0,
} from './constants';
import { GetWurDashboardPageQuery } from '#core/PageQuery/components/PageQuery';
import { camelCase } from 'change-case';
import { useInstitution } from '#core/Institution';
import { useAuth } from '#core/Auth';

export type ScenarioAndWurContract = {
  selectedMethodologyYear?: number;
  scenarioData?: ScenarioQuery | WurReporterInterimScenarioQuery;
  submissionData?: SubmissionWur2Query;
  wurData?: GetWurRatingAndScoreQuery;
  institutionId?: string;
  scenarioLoading: boolean;
  submissionLoading: boolean;
  wurLoading: boolean;
  currentYear: number;
  peers: Peer[];
  selectedPeers: Peer[];
  peersRegions: string[];
  setSelectedPeers: (peers: Peer[] | ((peers: Peer[]) => Peer[])) => void;
  setSelectedMethodologyYear: (year: number) => void;
  levelDefinitions: LevelDefinitionsObjectType;
  availableMethodologiesYears: number[];
};

export const defaultScenarioAndWurContext = Object.freeze({
  selectedMethodologyYear: undefined,
  scenarioData: {},
  submissionData: {},
  wurData: {},
  institutionId: '',
  scenarioLoading: true,
  submissionLoading: true,
  wurLoading: true,
  currentYear: new Date().getFullYear(),
  peers: [],
  selectedPeers: [],
  peersRegions: [],
  setSelectedPeers: () => {},
  setSelectedMethodologyYear: () => {},
  levelDefinitions: {},
  availableMethodologiesYears: [],
});

const ScenarioAndWurContext = createContext<ScenarioAndWurContract>(
  defaultScenarioAndWurContext,
);

export const useScenarioAndWurData = () =>
  useContext<ScenarioAndWurContract>(ScenarioAndWurContext);

const ScenarioAndWurDataProvider: React.FC<any> = (props) => {
  const { isAdmin } = useAuth();
  const flags = useFlags();

  const showYearsBeforeEmbargoEnd =
    isAdmin && flags[camelCase('include2026InMethodologyYears')];

  const { hasResourcePermissionNew } = usePermissions();
  const {
    children,
    pageConfig: { isWurReporterInterim },
  } = props;

  const { data } = useQuery<WurDashboardPageQueryQuery>(
    GetWurDashboardPageQuery,
    {
      variables: {
        releaseNames: ['wur'],
        responseFields: ['LOCATIONS'],
      },
    },
  );

  const { releases } = data || {};

  const possibleMethodologyYears = isWurReporterInterim
    ? POSSIBLE_METHODOLOGY_YEARS_WUR_REPORTER
    : POSSIBLE_METHODOLOGY_YEARS_WUR_EXPLORE;

  const filteredMethodologyYears = releases
    ? releases
        .filter((release) => {
          const releaseIsPastEmbargoEnd =
            new Date().valueOf() > Date.parse(release.embargoEndDate).valueOf();

          return (
            possibleMethodologyYears.includes(release.year) &&
            (showYearsBeforeEmbargoEnd || releaseIsPastEmbargoEnd)
          );
        })
        .map((release) => release.year)
    : [];

  const EXPLORE_METHODOLOGY_3_ENABLED =
    flags[camelCase('exploreMethodology3Enabled')];

  const { institution } = useInstitution();

  const availableMethodologiesYears: number[] = [];

  filteredMethodologyYears.forEach((year) => {
    if (
      EXPLORE_METHODOLOGY_3_ENABLED &&
      (isWurReporterInterim ||
        (!isWurReporterInterim &&
          hasResourcePermissionNew({
            action: 'explore:viewPage',
            year,
            institutionId: institution?.institutionId,
          })))
    ) {
      availableMethodologiesYears.push(year);
    }
  });

  const mostRecentAvailableYear = Math.max(...availableMethodologiesYears);

  const { scenarioId } = useParams();

  const { institutionId } = institution;

  // Methodology used before 2024 is WUR 2.0
  const [selectedMethodologyYear, setSelectedMethodologyYear] = useState<
    number
  >(mostRecentAvailableYear);

  const getSubmissionQuery =
    selectedMethodologyYear < YEAR_OF_START_OF_WUR_3_0
      ? getSubmissionQueryWur2
      : getSubmissionQueryWur3;

  const { data: submissionData, loading: submissionLoading } = useQuery<
    SubmissionWur2Query | SubmissionWur3Query
  >(getSubmissionQuery, {
    fetchPolicy: 'no-cache',
    variables: {
      institutionId,
      year: selectedMethodologyYear,
    },
  });

  const scenarioQuery = isWurReporterInterim
    ? getWurReporterInterimScenarioQuery
    : getScenarioQuery;

  const {
    data: scenarioData,
    loading: scenarioLoading,
    error: scenarioError,
  } = useQuery<ScenarioQuery | WurReporterInterimScenarioQuery>(
    scenarioQuery,
    scenarioQueryVariables(
      scenarioId,
      institutionId,
      !!isWurReporterInterim,
      'cache-and-network',
    ),
  );

  if (scenarioError) {
    throw scenarioError;
  }

  const scenario = isWurReporterInterim
    ? (scenarioData as WurReporterInterimScenarioQuery)
        ?.wurReporterInterimScenario
    : (scenarioData as ScenarioQuery)?.scenario;

  const { data: wurData, loading: wurLoading, error: wurError } = useQuery<any>(
    gql`
      query GetWURRatingScoreAndLevelDefinitions(
        $year: Int!
        $institutionId: String!
      ) {
        wurRelease(year: $year) {
          levelDefinitions {
            level2 {
              parentId
              levelId
              shortName
              longName
              name
              weighting
              type
              rankingYear
            }
            level3 {
              parentId
              levelId
              shortName
              longName
              name
              weighting
              type
              rankingYear
            }
          }
          subject(name: "overall") {
            result(institutionId: $institutionId) {
              score {
                higher
                lower
              }
              rank {
                higher
                lower
              }
            }
          }
        }
      }
    `,
    wurQueryVariables(selectedMethodologyYear, institutionId),
  );

  if (wurError) {
    throw wurError;
  }

  const currentYear = getScenarioMethodologyYear(scenario?.methodology);

  const productTitle = isWurReporterInterim ? 'WurReporterInterim' : 'DataPlus';

  const getProduct = useCallback(
    (productName: string) => {
      const { products: institutionProducts } = institution;
      return (institutionProducts || []).find(
        ({ name }) => name.toLowerCase() === productName.toLowerCase(),
      );
    },
    [institution],
  );

  const peersFullInfo = getProduct(productTitle);

  const group = peersFullInfo?.institutionGroups?.find(
    ({ name }) => name === 'peers',
  );

  const peers = peersFullInfo?.institutionGroups[0]?.institutions || [];

  const [selectedPeers, setSelectedPeers] = useSessionStorage<Peer[]>(
    isWurReporterInterim ? 'selectedWurInterimPeers' : 'selectedPeers',
    peers,
  );

  const peerContinents =
    group?.institutions.reduce(
      (acc, institutionInfo) =>
        acc.indexOf(institutionInfo.continent) >= 0
          ? acc
          : [...acc, institutionInfo.continent],
      [] as string[],
    ) || [];
  const peerCountries =
    group?.institutions.reduce(
      (acc, institutionInfo) =>
        acc.indexOf(institutionInfo.country) >= 0
          ? acc
          : [...acc, institutionInfo.country],
      [] as string[],
    ) || [];

  const peersRegions = ['Worldwide', ...peerContinents, ...peerCountries];

  // @ts-ignore
  const levelDefinitions = wurData?.wurRelease?.levelDefinitions || {};

  return (
    <ScenarioAndWurContext.Provider
      value={{
        selectedMethodologyYear,
        scenarioData,
        submissionData,
        wurData,
        institutionId,
        scenarioLoading,
        submissionLoading,
        wurLoading,
        currentYear,
        peers,
        selectedPeers,
        peersRegions,
        levelDefinitions,
        availableMethodologiesYears,
        setSelectedPeers,
        setSelectedMethodologyYear,
      }}
    >
      {children}
    </ScenarioAndWurContext.Provider>
  );
};

export default ScenarioAndWurDataProvider;
